import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useMatch } from 'react-router';

export interface SectionNavLink {
  /** Pfad unterhalb von basePath, ohne führenden Schrägstrich */
  path: string;
  label: string;
}

export interface SectionNavGroup {
  key: string;
  label: string;
  links: SectionNavLink[];
}

/** Aufgeklappte Gruppen der Navigation (KON-106). Anfangs nur die Gruppen aus defaultOpen, danach gilt, was der Benutzer auf- oder zuklappt. Der Browser merkt es sich. */
function useOpenGroups(storageKey: string, defaultOpen: string[]) {
  const [open, setOpen] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return new Set(stored ? (JSON.parse(stored) as string[]) : defaultOpen);
    } catch {
      return new Set(defaultOpen);
    }
  });
  const update = (change: (next: Set<string>) => void) =>
    setOpen((current) => {
      const next = new Set(current);
      change(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {
        // Ohne Speicher im Browser bleibt die Wahl nur bis zum Neuladen
      }
      return next;
    });
  const toggle = (key: string) => update((next) => (next.has(key) ? next.delete(key) : next.add(key)));
  const ensure = (key: string) => {
    if (!open.has(key)) update((next) => next.add(key));
  };
  return { open, toggle, ensure };
}

interface Props {
  title: string;
  /** z. B. /einrichtung oder /konto */
  basePath: string;
  /** Alle sichtbaren Gruppen, wenn query leer ist, sonst nur die Treffer */
  groupsFor: (query: string) => SectionNavGroup[];
  storageKey: string;
  defaultOpen: string[];
  navLabel: string;
  searchLabel: string;
  backLabel: string;
  /** Steht statt der Navigation da, wenn es keine einzige Gruppe gibt */
  empty?: ReactNode;
}

/**
 * Seitennavigation mit Suche und aufklappbaren Gruppen, wie sie die Einrichtung (KON-105, KON-106) und „Mein Konto“ (KON-122) nutzen. Jede Gruppe klappt per
 * Klick auf ihren Titel auf und bleibt offen, bis man wieder darauf klickt. Öffnet man einen Bereich direkt über die Adresse, klappt seine Gruppe mit auf.
 * Unter 900 Pixel steht die Navigation allein auf basePath, ein Bereich hat dort einen Link zurück.
 */
export function SectionNavLayout({ title, basePath, groupsFor, storageKey, defaultOpen, navLabel, searchLabel, backLabel, empty }: Props) {
  const atIndex = !!useMatch(basePath);
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');
  const { open, toggle, ensure } = useOpenGroups(storageKey, defaultOpen);
  const all = groupsFor('');
  const groups = query.trim() ? groupsFor(query) : all;
  const inside = (path: string) => pathname === `${basePath}/${path}` || pathname.startsWith(`${basePath}/${path}/`);
  const currentGroup = all.find((g) => g.links.some((l) => inside(l.path)))?.key;

  useEffect(() => {
    if (currentGroup) ensure(currentGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup]);

  if (all.length === 0 && empty) return <>{empty}</>;

  const searching = !!query.trim();
  const idPrefix = basePath.replace(/\W/g, '') || 'nav';
  return (
    <div className={`setup${atIndex ? ' setup--index' : ''}`}>
      <aside className="setup__nav" aria-label={navLabel}>
        <h1 className="setup__home">{title}</h1>
        <input className="input setup__search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`${searchLabel} …`} aria-label={searchLabel} />
        {groups.map((g) => {
          // Beim Suchen sind alle Gruppen mit Treffern offen
          const isOpen = searching || open.has(g.key);
          return (
            <div key={g.key} className="setup__group">
              <button type="button" className="setup__group-label" aria-expanded={isOpen} aria-controls={`${idPrefix}-group-${g.key}`} onClick={() => toggle(g.key)} disabled={searching}>
                <span className="setup__chevron" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                {g.label}
              </button>
              <div id={`${idPrefix}-group-${g.key}`} className="setup__links" hidden={!isOpen}>
                {g.links.map((l) => <NavLink key={l.path} to={`${basePath}/${l.path}`}>{l.label}</NavLink>)}
              </div>
            </div>
          );
        })}
        {groups.length === 0 && <p className="muted small">Nichts gefunden.</p>}
      </aside>
      <div className="setup__main">
        {!atIndex && <p className="setup__back"><Link to={basePath} className="back-link">← {backLabel}</Link></p>}
        <Outlet />
      </div>
    </div>
  );
}

/** Auf breiten Bildschirmen geht es von basePath gleich zum ersten Bereich, auf schmalen steht dort nur die Navigation */
export function wideScreen() {
  return typeof window !== 'undefined' && window.matchMedia('(min-width: 900px)').matches;
}
